// frontend/src/pages/Partner.tsx
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Stack,
  SimpleGrid,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  InputGroup,
  Input,
  InputLeftElement,
  Icon,
  Badge,
  Skeleton,
  useToast,
  Avatar,
  Divider,
  Tooltip,
  Button,
  Container,
  Tag
} from '@chakra-ui/react'
import {
  FiUsers,
  FiUser,
  FiPhone,
  FiSearch,
  FiCalendar,
  FiTrendingUp,
  FiDollarSign,
  FiClock
} from 'react-icons/fi'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { getPartnerList } from '../services/apiService'
import type { AxiosResponse } from 'axios'

type DirectUser = {
  id: number
  account: string
  username: string
  email: string | null
  phone: string | null
  grade: string | null
  serviceAt: string | null
  referralAt: string | null
  subscriptionAt: string | null
  createdAt: string
  updatedAt: string
}

type PartnerSalesItem = {
  userId: number
  account: string
  username: string
  salesUSDT: string
  salesCount: number
  lastSaleAt: string | null
}

type RewardItem = {
  id: number
  usdtAmount: string
  byUserId: number
  byHistoryId: number
  note1: string | null
  note2: string | null
  note3: string | null
  createdAt: string
  payoutScheduledDate: string | null
  payoutStatus: 'PENDING' | 'PAID' | string | null
}

type MyRewards = {
  totalRewardUSDT: string
  pendingRewardUSDT: string
  items: RewardItem[]
}

type PartnerData = {
  directList: DirectUser[]
  directCount: number
  totalDescendantCount: number
  activeDirectList: DirectUser[]
  activeDirectCount: number
  partnerSales: PartnerSalesItem[]
  myRewards: MyRewards
}

type CommonResponse = {
  statusCode: number
  message: string
  data: PartnerData
}

const isValidFuture = (iso?: string | null) => {
  if (!iso) return false
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return false
  return t > Date.now()
}

const formatDate = (iso?: string | null) => {
  if (!iso) return '-'
  const d = dayjs(iso)
  if (!d.isValid()) return '-'
  return d.format('YYYY.MM.DD HH:mm:ss')
}

const formatUSDT = (v?: string | number | null) => {
  if (v === null || v === undefined) return '0'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return String(v)
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8
  })
}

// 랜덤 계정명 생성 함수
const generateRandomAccount = (): string => {
  const prefixes = ['user', 'trader', 'crypto', 'xrp', 'btc', 'eth', 'invest', 'trade', 'coin', 'wallet']
  const suffixes = ['pro', 'master', 'king', 'star', 'gold', 'vip', 'elite', 'prime', 'max', 'plus']
  const numbers = Math.floor(100 + Math.random() * 9000) // 100 ~ 9999
  
  // 50% 확률로 prefix + number, 50% 확률로 prefix + suffix + number
  if (Math.random() > 0.5) {
    return `${prefixes[Math.floor(Math.random() * prefixes.length)]}${numbers}`
  } else {
    return `${prefixes[Math.floor(Math.random() * prefixes.length)]}${suffixes[Math.floor(Math.random() * suffixes.length)]}${numbers}`
  }
}

// 데모 모드용 더미 데이터 생성 함수
const generateDemoData = (): PartnerData => {
  const now = new Date()
  const directList: DirectUser[] = []
  const partnerSales: PartnerSalesItem[] = []
  const rewardItems: RewardItem[] = []

  // 직접 추천 파트너 20개 생성
  const names = [
    '김철수', '이영희', '박민수', '최지영', '정수진',
    '강동원', '윤서연', '임태현', '한소희', '조진웅',
    '오세훈', '신민아', '류준열', '김태리', '이동휘',
    '전지현', '송혜교', '현빈', '손예진', '공유'
  ]
  
  // 랜덤 계정명 20개 생성 (중복 방지)
  const accounts: string[] = []
  const accountSet = new Set<string>()
  while (accounts.length < 20) {
    const account = generateRandomAccount()
    if (!accountSet.has(account)) {
      accountSet.add(account)
      accounts.push(account)
    }
  }

  for (let i = 0; i < 20; i++) {
    const daysAgo = Math.floor(Math.random() * 365)
    const createdAt = dayjs(now).subtract(daysAgo, 'day').toISOString()
    
    // 등급별로 분배 (SECRET: 2, VIP: 5, NORMAL: 8, 일반: 5)
    let serviceAt: string | null = null
    let referralAt: string | null = null
    let subscriptionAt: string | null = null

    if (i < 2) {
      // SECRET
      subscriptionAt = dayjs(now).add(30 + Math.random() * 60, 'day').toISOString()
    } else if (i < 7) {
      // VIP
      referralAt = dayjs(now).add(30 + Math.random() * 60, 'day').toISOString()
    } else if (i < 15) {
      // NORMAL
      serviceAt = dayjs(now).add(30 + Math.random() * 60, 'day').toISOString()
    }

    directList.push({
      id: i + 1,
      account: accounts[i],
      username: names[i],
      email: `${accounts[i]}@example.com`,
      phone: `010${String(Math.floor(1000 + Math.random() * 9000))}${String(Math.floor(1000 + Math.random() * 9000))}`,
      grade: null,
      serviceAt,
      referralAt,
      subscriptionAt,
      createdAt,
      updatedAt: createdAt
    })
  }

  // 파트너 매출 12개 생성
  const salesAccounts = accounts.slice(0, 12)
  const salesNames = names.slice(0, 12)
  for (let i = 0; i < 12; i++) {
    const salesAmount = Math.floor(5000 + Math.random() * 50000) // 5,000 ~ 55,000 USDT
    const salesCount = Math.floor(3 + Math.random() * 20) // 3 ~ 23 건
    const daysAgo = Math.floor(Math.random() * 30)
    const lastSaleAt = dayjs(now).subtract(daysAgo, 'day').toISOString()

    partnerSales.push({
      userId: i + 1,
      account: salesAccounts[i],
      username: salesNames[i],
      salesUSDT: String(salesAmount),
      salesCount,
      lastSaleAt
    })
  }

  // 나의 보상 15개 생성
  const rewardNotes = [
    '레퍼럴 보상',
    '매출 보너스',
    '추천인 보상',
    '파트너 수수료',
    '영업 인센티브',
    '추가 보상',
    '특별 보상',
    '월간 보너스',
    '계절 보너스',
    '연말 보너스'
  ]
  
  let totalReward = 0
  let pendingReward = 0

  for (let i = 0; i < 15; i++) {
    const amount = Math.floor(1000 + Math.random() * 5000) // 1,000 ~ 6,000 USDT
    const isPending = Math.random() > 0.4 // 60% 확률로 대기
    const daysAgo = Math.floor(Math.random() * 90)
    const createdAt = dayjs(now).subtract(daysAgo, 'day').toISOString()
    const payoutScheduledDate = isPending 
      ? dayjs(now).add(7 + Math.random() * 14, 'day').format('YYYY-MM-DD')
      : null

    totalReward += amount
    if (isPending) {
      pendingReward += amount
    }

    rewardItems.push({
      id: i + 1,
      usdtAmount: String(amount),
      byUserId: Math.floor(1 + Math.random() * 20),
      byHistoryId: Math.floor(1000 + Math.random() * 9000),
      note1: rewardNotes[Math.floor(Math.random() * rewardNotes.length)],
      note2: `파트너 ${accounts[Math.floor(Math.random() * 20)]}님의 매출`,
      note3: null,
      createdAt,
      payoutScheduledDate,
      payoutStatus: isPending ? 'PENDING' : 'PAID'
    })
  }

  return {
    directList,
    directCount: 161,
    totalDescendantCount: 3320,
    activeDirectList: directList.filter(u => 
      isValidFuture(u.serviceAt) || isValidFuture(u.referralAt) || isValidFuture(u.subscriptionAt)
    ),
    activeDirectCount: 117,
    partnerSales,
    myRewards: {
      totalRewardUSDT: '240000',
      pendingRewardUSDT: '30000',
      items: rewardItems
    }
  }
}

const Partner = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const [auth] = useRecoilState(authState)

  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<PartnerData | null>(null)
  const [query, setQuery] = useState('')

  const load = async () => {
    try {
      setLoading(true)
      const res = (await getPartnerList()) as AxiosResponse<CommonResponse>
      if (res?.data?.statusCode === 200) {
        setData(res.data.data)
      } else {
        throw new Error(
          res?.data?.message || t('파트너 정보를 불러오지 못했습니다.')
        )
      }
    } catch (err: any) {
      toast({
        status: 'error',
        title: t('불러오기 실패'),
        description: err?.message || t('파트너 정보를 불러오지 못했습니다.'),
        duration: 4000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 데모 모드 체크 (XrpKorea 계정인 경우 하드코딩된 값 표시)
  const isDemoMode = auth?.account === 'XrpKorea'

  // 데모 모드일 때 더미 데이터 생성
  const demoData = useMemo(() => {
    if (isDemoMode) {
      return generateDemoData()
    }
    return null
  }, [isDemoMode])

  // 표시할 데이터 (데모 모드면 더미 데이터, 아니면 실제 데이터)
  const displayData = isDemoMode ? demoData : data

  const filtered = useMemo(() => {
    const list = displayData?.directList || []
    if (!query.trim()) return list
    const q = query.toLowerCase()
    return list.filter((u) =>
      [u.account, u.username, u.phone ?? '', u.email ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [displayData, query])

  // 지급상태 표시(사람이 읽는 언어로)
  const payoutLabel = (status?: string | null) => {
    if (status === 'PENDING') return t('대기')
    if (status === 'PAID') return t('지급완료')
    return status || 'UNKNOWN'
  }

  // 데모 모드용 하드코딩된 통계 값
  const displayStats = {
    directCount: isDemoMode ? 161 : data?.directCount ?? 0,
    activeDirectCount: isDemoMode ? 117 : data?.activeDirectCount ?? 0,
    totalDescendantCount: isDemoMode ? 3320 : data?.totalDescendantCount ?? 0,
    totalRewardUSDT: isDemoMode ? '240000' : data?.myRewards?.totalRewardUSDT ?? '0',
    pendingRewardUSDT: isDemoMode ? '30000' : data?.myRewards?.pendingRewardUSDT ?? '0'
  }

  return (
    <DefaultLayout>
      <Container maxW="7xl" py={8}>
        <Stack spacing={6}>
          {/* 헤더 */}
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Icon as={FiUsers} boxSize={6} />
                <Text fontSize="2xl" fontWeight="bold">
                  {t('파트너 현황')}
                </Text>
              </HStack>
              <Button
                variant="ghost"
                size="sm"
                onClick={load}
                isLoading={loading}
              >
                {t('새로고침')}
              </Button>
            </HStack>
            <Text color="gray.500">
              {t(
                '직접 추천, 활성 파트너, 산하 전체 규모와 파트너 매출/나의 보상을 한 눈에 확인하세요.'
              )}
            </Text>
          </VStack>

          {/* 통계 카드 */}
          <SimpleGrid columns={{ base: 1, md: 3, xl: 5 }} gap={4}>
            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('직접 추천 파트너')}
              </StatLabel>
              <Skeleton isLoaded={!loading || isDemoMode}>
                <StatNumber fontSize="3xl">{displayStats.directCount}</StatNumber>
              </Skeleton>
              <StatHelpText>{t('내가 직접 초대한 파트너')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('활성화 추천 파트너')}
              </StatLabel>
              <Skeleton isLoaded={!loading || isDemoMode}>
                <StatNumber fontSize="3xl">
                  {displayStats.activeDirectCount}
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('봇 이용일자가 유효한 파트너')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">
                {t('산하 전체 파트너 수')}
              </StatLabel>
              <Skeleton isLoaded={!loading || isDemoMode}>
                <StatNumber fontSize="3xl">
                  {displayStats.totalDescendantCount}
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('내 트리 전체(모든 레벨)')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">{t('나의 보상 총액')}</StatLabel>
              <Skeleton isLoaded={!loading || isDemoMode}>
                <StatNumber fontSize="3xl">
                  {formatUSDT(displayStats.totalRewardUSDT)} USDT
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('영업활동에 대한 누적 보상')}</StatHelpText>
            </Stat>

            <Stat
              p={5}
              rounded="2xl"
              borderWidth="1px"
              bg="whiteAlpha.700"
              backdropFilter="auto"
              backdropBlur="4px"
              shadow="sm"
            >
              <StatLabel fontWeight="semibold">{t('대기 중 보상')}</StatLabel>
              <Skeleton isLoaded={!loading || isDemoMode}>
                <StatNumber fontSize="3xl">
                  {formatUSDT(displayStats.pendingRewardUSDT)} USDT
                </StatNumber>
              </Skeleton>
              <StatHelpText>{t('영업활동에 대한 대기중인 보상')}</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* 검색 */}
          <Box>
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} />
              </InputLeftElement>
              <Input
                placeholder={t('계정/이름/전화/이메일 검색')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                rounded="xl"
              />
            </InputGroup>
          </Box>

          {/* 직접 추천 리스트 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Text fontSize="xl" fontWeight="bold">
                {t('직접 추천 파트너')}
              </Text>
              <Badge colorScheme="blue" rounded="lg">
                {filtered.length} / {displayStats.directCount}
              </Badge>
              <Tag size="sm" colorScheme="green">
                {t('활성 {{count}}', { count: displayStats.activeDirectCount })}
              </Tag>
            </HStack>
            <Divider />

            {/* 로딩 스켈레톤 */}
            {(loading && !isDemoMode) && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(6)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}

            {/* 비어있을 때 */}
            {(!loading || isDemoMode) && filtered.length === 0 && (
              <Box
                p={8}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t(
                  '아직 직접 추천한 사용자가 없거나, 검색 조건에 맞는 결과가 없어요.'
                )}
              </Box>
            )}

            {/* 카드 그리드 */}
            {(!loading || isDemoMode) && filtered.length > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {filtered.map((u) => (
                  <Box
                    key={u.id}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                    _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                    transition="all 0.2s ease"
                  >
                    <HStack align="flex-start" spacing={4}>
                      <Avatar name={u.account} />
                      <VStack align="stretch" spacing={1} flex={1}>
                        <HStack justify="space-between">
                          <HStack>
                            <Text fontWeight="bold">{u.account || '-'}</Text>
                          </HStack>
                          {/* 등급/상태 뱃지 (subscription/referral/service) */}
                          {(() => {
                            const isSecret = isValidFuture(u.subscriptionAt)
                            const isVip =
                              !isSecret && isValidFuture(u.referralAt)
                            const isNomal =
                              !isSecret && !isVip && isValidFuture(u.serviceAt)
                            const label = isSecret
                              ? t('SECRET')
                              : isVip
                              ? t('VIP')
                              : isNomal
                              ? t('NOMAL')
                              : t('NORMAL')
                            const colorScheme = isSecret
                              ? 'red'
                              : isVip
                              ? 'yellow'
                              : isNomal
                              ? 'blue'
                              : 'gray'
                            return (
                              <Badge
                                colorScheme={colorScheme}
                                variant="subtle"
                                rounded="md"
                              >
                                {label}
                              </Badge>
                            )
                          })()}
                        </HStack>

                        <HStack color="gray.600">
                          <Icon as={FiUser} />
                          <Text fontFamily="mono" fontSize="sm">
                            {u.username}
                          </Text>
                        </HStack>
                        <HStack color="gray.600">
                          <Icon as={FiPhone} />
                          <Text fontSize="sm">{u.phone || '-'}</Text>
                        </HStack>

                        <Divider my={2} />

                        <VStack
                          align="stretch"
                          spacing={1}
                          color="gray.600"
                          fontSize="sm"
                        >
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('서비스 유효일')}>
                              <Text>
                                {t('서비스')}: {formatDate(u.serviceAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('레퍼럴 수익 유효일')}>
                              <Text>
                                {t('레퍼럴')}: {formatDate(u.referralAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('구독료 수익 유효일')}>
                              <Text>
                                {t('구독료')}: {formatDate(u.subscriptionAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                          <HStack>
                            <Icon as={FiCalendar} />
                            <Tooltip label={t('가입일')}>
                              <Text>
                                {t('가입')}: {formatDate(u.createdAt)}
                              </Text>
                            </Tooltip>
                          </HStack>
                        </VStack>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>

          {/* 파트너 매출 섹션 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Icon as={FiTrendingUp} />
              <Text fontSize="xl" fontWeight="bold">
                {t('파트너 매출')}
              </Text>
              <Tag size="sm" colorScheme="purple">
                {t('합계 {{amount}} USDT', {
                  amount: formatUSDT(
                    (displayData?.partnerSales || []).reduce(
                      (s, x) => s + Number(x.salesUSDT || 0),
                      0
                    )
                  )
                })}
              </Tag>
            </HStack>
            <Divider />
            {(loading && !isDemoMode) && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}
            {(!loading || isDemoMode) && (displayData?.partnerSales?.length ?? 0) === 0 && (
              <Box
                p={6}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t('파트너 매출 데이터가 없습니다.')}
              </Box>
            )}
            {(!loading || isDemoMode) && (displayData?.partnerSales?.length ?? 0) > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {displayData!.partnerSales.map((s) => (
                  <Box
                    key={s.userId}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack>
                        <Avatar name={s.account} size="sm" />
                        <VStack spacing={0} align="start">
                          <Text fontWeight="bold">{s.account}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {s.username}
                          </Text>
                        </VStack>
                      </HStack>
                      <Tag colorScheme="blue">
                        {t('{{count}} 건', { count: s.salesCount })}
                      </Tag>
                    </HStack>
                    <HStack color="gray.700" mb={1}>
                      <Icon as={FiDollarSign} />
                      <Text fontWeight="semibold">
                        {formatUSDT(s.salesUSDT)} USDT
                      </Text>
                    </HStack>
                    <HStack color="gray.600" fontSize="sm">
                      <Icon as={FiClock} />
                      <Text>
                        {t('마지막 매출')}: {formatDate(s.lastSaleAt)}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>

          {/* 나의 보상 섹션 */}
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Icon as={FiDollarSign} />
              <Text fontSize="xl" fontWeight="bold">
                {t('나의 보상')}
              </Text>
              <Tag size="sm" colorScheme="teal">
                {t('총 {{amount}} USDT', {
                  amount: formatUSDT(displayStats.totalRewardUSDT)
                })}
              </Tag>
              <Tag size="sm" colorScheme="orange">
                {t('대기 {{amount}} USDT', {
                  amount: formatUSDT(displayStats.pendingRewardUSDT)
                })}
              </Tag>
            </HStack>
            <Divider />
            {(loading && !isDemoMode) && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {[...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <Skeleton height="20px" mb={3} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                ))}
              </SimpleGrid>
            )}
            {(!loading || isDemoMode) && (displayData?.myRewards?.items?.length ?? 0) === 0 && (
              <Box
                p={6}
                borderWidth="1px"
                rounded="2xl"
                textAlign="center"
                color="gray.500"
              >
                {t('보상 이력이 없습니다.')}
              </Box>
            )}
            {(!loading || isDemoMode) && (displayData?.myRewards?.items?.length ?? 0) > 0 && (
              <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={4}>
                {displayData!.myRewards.items.map((r) => (
                  <Box
                    key={r.id}
                    p={5}
                    borderWidth="1px"
                    rounded="2xl"
                    shadow="sm"
                  >
                    <HStack justify="space-between" mb={2}>
                      <HStack>
                        <Icon as={FiDollarSign} />
                        <Text fontWeight="bold">
                          {formatUSDT(r.usdtAmount)} USDT
                        </Text>
                      </HStack>
                      <Tag
                        colorScheme={
                          r.payoutStatus === 'PENDING' ? 'orange' : 'green'
                        }
                      >
                        {payoutLabel(r.payoutStatus)}
                      </Tag>
                    </HStack>
                    {r.note1 && (
                      <Text fontSize="sm" color="gray.700" mb={1}>
                        {r.note1}
                      </Text>
                    )}
                    {r.note2 && (
                      <Text fontSize="xs" color="gray.500" mb={1}>
                        {r.note2}
                      </Text>
                    )}
                    <VStack
                      align="start"
                      spacing={1}
                      color="gray.600"
                      fontSize="sm"
                      mt={2}
                    >
                      <HStack>
                        <Icon as={FiCalendar} />
                        <Text>
                          {t('발생일')}: {formatDate(r.createdAt)}
                        </Text>
                      </HStack>
                      <HStack>
                        <Icon as={FiClock} />
                        <Text>
                          {t('지급 예정')}: {r.payoutScheduledDate ?? '-'}
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>
                ))}
              </SimpleGrid>
            )}
          </VStack>
        </Stack>
      </Container>
    </DefaultLayout>
  )
}

export default Partner
